<template>
  <div class="page-container">
    <h2 class="page-title">优惠券明细</h2>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-form :inline="true">
        <el-form-item label="搜索">
          <el-input v-model="searchKey" placeholder="优惠券名称/ID" clearable @keyup.enter="handleSearch">
            <template #append>
              <el-button @click="handleSearch">
                <el-icon>
                  <Search />
                </el-icon>
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe v-loading="loading">
      
      <el-table-column prop="name" label="名称" width="180" />

        <!-- 新增用户昵称列 -->
  <el-table-column prop="nickName" label="所属用户" width="150">
    <template #default="{ row }">
      <span v-if="row.nickName">{{ row.nickName }}</span>
      <span v-else class="text-gray">未设置</span>
    </template>
  </el-table-column>

      <el-table-column label="类型" width="100">
        <template #default="{row}">
          {{ typeMap[row.type_id] }}
        </template>
      </el-table-column>
      <el-table-column label="优惠金额" width="120">
        <template #default="{row}">￥{{ row.coupon_money }}</template>
      </el-table-column>
      <el-table-column prop="money_limit" label="使用条件" width="120">
        <template #default="{row}">满￥{{ row.money_limit }}</template>
      </el-table-column>
      <el-table-column label="有效期" width="420">
        <template #default="{row}">
          {{ formatDate(row.valid_bg_time) }} ~ {{ formatDate(row.valid_end_time) }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{row}">
          <el-tag :type="row.status === 1 ? 'success' : ''">
            {{ statusMap[row.status] }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="领取时间" />
      <el-table-column label="操作" fixed="right" width="150">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="openEditDialog(row)">编辑</el-button>
          <el-popconfirm title="确定删除该记录？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination 
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="dialogVisible" title="编辑优惠券状态" width="500px">
      <el-form :model="formData" label-width="80px">
        <el-form-item label="状态">
          <el-radio-group v-model="formData.status">
            <el-radio :label="0">待使用</el-radio>
            <el-radio :label="1">已使用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import Cookies from 'js-cookie'
import config from '@/config'
import axios from 'axios'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id

// 状态映射
const typeMap = { 1: '领取', 2: '赠送', 3: '兑换' }
const statusMap = { 0: '待使用', 1: '已使用' }

// 数据相关
const loading = ref(false)
const searchKey = ref('')
const dialogVisible = ref(false)
const tableData = ref([])
const formData = reactive({
  id: null,
  status: 0
})

// 分页配置
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 加载数据
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      store_id: storeId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/coupon_api.php`, { params })
    
    if (data.code === 200) {
      tableData.value = data.data.list
      pagination.total = data.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('数据加载失败')
  } finally {
    loading.value = false
  }
}

// 搜索处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

// 打开编辑弹窗
const openEditDialog = (row) => {
  formData.id = row.id
  formData.status = row.status
  dialogVisible.value = true
}

// 提交编辑
const submitForm = async () => {
  try {
    await axios.put(`${config.BASE_URL}/shop/api/coupon_api.php`, {
      ...formData,
      store_id: storeId
    })
    
    ElMessage.success('更新成功')
    dialogVisible.value = false
    fetchData()
  } catch (error) {
    ElMessage.error('更新失败')
  }
}

// 删除记录
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/coupon_api.php`, {
      data: { id, store_id: storeId }
    })
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}

// 日期格式化
const formatDate = (dateString) => {
  return dateString ? new Date(dateString).toLocaleString() : '-'
}

// 初始化加载
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-title {
  margin-bottom: 20px;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.text-gray {
  color: #999;
  font-style: italic;
}
</style>